import {
  isValidBirthday,
  isValidEmail,
  isValidPhone,
} from '../validators';

type UnknownObject = { [key: string]: unknown };

/**
 * Determines whether name is valid
 *
 * @name  isNameValid
 * @param  {String}  name
 * @returns {Boolean}
 */
export function isNameValid(name: string): boolean {
  return !!name;
}

function formatEightDigit(number: string): string {
  let shift = 0;
  let frontSpace = '';
  if (/^1/.test(number)) {
    shift = 1;
    frontSpace = ' ';
  } else if (/^\+1/.test(number)) {
    shift = 2;
    frontSpace = ' ';
  }
  return `${number.slice(0, shift) + frontSpace}(${number.slice(shift, 3 + shift)}) ${number.slice(3 + shift, 6 + shift)}-${number.slice(6 + shift)}`;
}

const validErrorKeys = <const>[
  'name',
  'email',
  'birthday',
];
type ValidErrorKeysType = typeof validErrorKeys[number];
type UIErrors = { [key in ValidErrorKeysType]?: true };

function mapErrorsForUI(errors: UnknownObject) {
  const uiErrors: UIErrors = {};

  Object.keys(errors).forEach(errorType => {
    if (validErrorKeys.includes(errorType as any)) {
      uiErrors[errorType] = true;
    }
  });

  return uiErrors;
}

function getDefaultErrorForOperation(type: string, exceptionHandler?: () => void) {
  if (type === 'add') {
    return 'Error saving client';
  } if (type === 'update') {
    return 'Error editing client';
  } if (type === 'delete') {
    return 'Error deleting client';
  }
  exceptionHandler?.();
  return undefined;
}
const serverErrorKey = 'serverError';
export type ServerError = { [serverErrorKey]: string };

function createServerError(message: string): ServerError {
  return {
    [serverErrorKey]: message,
  };
}

/**
 * Determines whether passed email is valid
 *
 * @name  isEmailValid
 * @param  {String}  email
 * @returns {Boolean}
 */
export function isEmailValid(email: string): boolean {
  if (email) {
    return isValidEmail(email);
  }
  return true;
}

/**
 * Determines whether passed birthday is valid
 *
 * @name  isBirthdayValid
 * @param  {String} birthday Formatted "MM / DD"
 * @returns {Boolean}
 */
export function isBirthdayValid(birthday: string): boolean {
  return birthday ? isValidBirthday(birthday) : true;
}

/**
 * Determines whether phone number is valid
 *
 * @name  isPhoneNumberValid
 * @param  {String}  number
 * @returns {Boolean}
 */
export function isPhoneNumberValid(number: string): boolean {
  return number ? isValidPhone(number) : true;
}

/**
 * Adds paretheses and dashes to phone number
 *
 * @name formatPhoneNumber
 * @param  {String} number Phone number
 * @returns {String} Formatted phone number
 */
export function formatPhoneNumber(number: string): string | undefined {
  if (!number) { return undefined; }

  const strippedNumber = number.replace(/\(|-|\)|\s/g, '');

  if (strippedNumber.length >= 8) {
    return formatEightDigit(strippedNumber);
  } if (strippedNumber.length < 8) {
    return strippedNumber;
  }

  return undefined;
}

/**
 * Formats a phone number to be digits only.
 *
 * @name formatPhoneNumberAsDigits
 * @param {String} number - phone number
 * @returns {String} Formatted phone number
 *
 * Does not handle country codes that are not '1' (USA)
 */
export function formatPhoneNumberAsDigits(number: string): string {
  // strip out any USA country code
  return number.replace(/^\+1|^1\(|^1\s\(|^1-|^1\s|\D+/g, '');
}

/**
 * Determines whether a given search string matches a phone number.
 *
 * @name matchWithSavedPhoneNumber
 * @param {String} number - phone number
 * @param {String} searchStr - search string being entered into search box
 * @returns {Boolean}
 */
export function matchWithSavedPhoneNumber(number: string, searchStr: string): boolean {
  // eslint-disable-next-line no-param-reassign
  number = number ? formatPhoneNumberAsDigits(number) : number;
  const origSearchStr = searchStr;
  // eslint-disable-next-line no-param-reassign
  searchStr = formatPhoneNumberAsDigits(searchStr);
  return origSearchStr.substring(0, 2) === '+1' ? number.indexOf(searchStr) === 0 : number.indexOf(searchStr) > -1;
}

/**
 * Takes errors returned from server and either maps them to the corresponding
 * input fields or returns a default error message based on the operation.
 * @param  {Object | Array | String} errors Error object, list or string
 * @param  {String} operationType Either 'add', 'update', or 'delete'
 * @param  {Function} exceptionHandler
 * @returns {Object}
 */
export function handleServerErrors(
  errors: UnknownObject | UnknownObject[] | string,
  operationType: string,
  exceptionHandler?: () => void,
): UIErrors | ServerError {
  if (typeof errors === 'string') {
    return createServerError(errors);
  }

  if (Array.isArray(errors)) {
    // eslint-disable-next-line no-param-reassign
    errors = errors[0];
  }

  const handledErrors = mapErrorsForUI(errors);

  return Object.keys(handledErrors).length
    ? handledErrors
    : createServerError(getDefaultErrorForOperation(operationType, exceptionHandler));
}
